import { Outlet } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import API_BASE_URL from '../../config';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { forceLogout, reasonFromError, LOGOUT_REASONS } from '../../utils/auth';

const SESSION_POLL_MS = 45000; // check session every 45s

export default function Layout({ showNotification }) {
  const [activeOrderCount, setActiveOrderCount] = useState(0);
  const [notification, setNotification] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // ── Active session poller ───────────────────────────────────
  // Detects remote logout (admin revoked this device from another browser)
  // even when there are no other API calls being made.
  useEffect(() => {
    let cancelled = false;

    const checkSession = async () => {
      const token = localStorage.getItem('ownerToken');
      if (!token || cancelled) return;
      try {
        const res = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        if (res.status === 401) {
          let reason = LOGOUT_REASONS.GENERIC;
          try {
            const body = await res.json();
            reason = reasonFromError({ response: { data: body } });
          } catch { /* ignore body parse error */ }
          forceLogout(reason);
        }
      } catch {
        // network error — ignore, will retry on next tick
      }
    };

    const interval = setInterval(checkSession, SESSION_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // ── Orders stats + SSE ──────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('ownerToken');
    if (!token) return;

    let sseConnectAttempts = 0;

    // Initial fetch of active order count
    fetch(`${API_BASE_URL}/orders/stats/today`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        if (r.status === 401) { forceLogout(LOGOUT_REASONS.REVOKED); return null; }
        return r.json();
      })
      .then(data => {
        if (data?.success) {
          setActiveOrderCount((data.data.new || 0) + (data.data.preparing || 0) + (data.data.ready || 0));
        }
      })
      .catch(() => {});

    // SSE for real-time updates
    const es = new EventSource(
      `${API_BASE_URL}/sse/orders?token=${encodeURIComponent(token)}`
    );

    es.addEventListener('open', () => { sseConnectAttempts = 0; });

    es.addEventListener('new_order', (e) => {
      const data = JSON.parse(e.data);
      setActiveOrderCount(c => c + 1);
      setNotification({ message: `New order #${data.orderNumber} — Table ${data.tableNumber}`, type: 'new_order' });
      setTimeout(() => setNotification(null), 5000);
      if (Notification.permission === 'granted') {
        new Notification('New Order!', {
          body: `Order #${data.orderNumber} from Table ${data.tableNumber}`,
          icon: '/favicon.ico'
        });
      }
    });

    es.addEventListener('order_updated', (e) => {
      const data = JSON.parse(e.data);
      if (data.status === 'Completed' || data.status === 'Cancelled') {
        setActiveOrderCount(c => Math.max(0, c - 1));
      }
    });

    // If SSE keeps failing right after connect, the token is likely invalid.
    // Verify against /auth/me which gives a definitive 401.
    es.onerror = () => {
      sseConnectAttempts++;
      if (sseConnectAttempts >= 2) {
        es.close();
        const tok = localStorage.getItem('ownerToken');
        if (tok) {
          fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${tok}` } })
            .then(r => { if (r.status === 401) forceLogout(LOGOUT_REASONS.REVOKED); })
            .catch(() => {});
        }
      }
    };

    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => es.close();
  }, []);

  return (
    <div className="app-shell">
      {mobileSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileSidebarOpen(false)} />
      )}
      <Sidebar
        activeOrderCount={activeOrderCount}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
        onCollapsedChange={setSidebarCollapsed}
      />
      <div className={`main-area ${sidebarCollapsed ? 'main-area-collapsed' : ''}`}>
        <TopBar
          activeOrderCount={activeOrderCount}
          onMenuToggle={() => setMobileSidebarOpen(o => !o)}
        />
        <main className="page-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={window.location.pathname}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
            >
              <Outlet context={{ showNotification, activeOrderCount, setActiveOrderCount }} />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* New order toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            className="order-toast"
            initial={{ opacity: 0, x: 80 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 80 }}
          >
            <i className="bi bi-bell-fill me-2"></i>
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
